import { useEffect, useMemo, useRef, useState } from "react"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import { startOfMonth, endOfMonth, sub, add, format } from "date-fns"

import { CircularProgress } from "@mui/material"
import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import { useFilteredActivitiesQuery } from "@/hooks/api/useActivityQuery"
import { getActivityColor } from "@/app/activity/list/utils"
import { useCalendarNav } from "@/app/home/hooks/useCalendarNav"
import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"
import type { Activity } from "@/types/activity"

import { useCalendarResize } from "../../hooks/useCalendarResize"

export default function ActivityCalendarMonthlyWidget() {
  const isPlayerAccount = createAccountStore((state) => state.info?.type === AccountTypeKey.Player)
  const calendarRef = useRef<FullCalendar>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { currentDate } = useCalendarNav()

  const [detailState, setDetailState] = useState<{
    open: boolean
    anchorEl: HTMLElement | null
    activity: Activity | null
  }>({ open: false, anchorEl: null, activity: null })
  const openDetail = (activity: Activity, anchorEl: HTMLElement) => {
    if (isPlayerAccount) return
    setDetailState({ open: true, anchorEl, activity })
  }
  const closeDetail = () => setDetailState((s) => ({ ...s, open: false }))

  useCalendarResize(calendarRef, containerRef)

  const dateStart = startOfMonth(currentDate)
  const dateEnd = endOfMonth(currentDate)

  const { data: activityList, isLoading } = useFilteredActivitiesQuery({
    start: sub(dateStart, { days: 6 }).valueOf(),
    end: add(dateEnd, { days: 7 }).valueOf(),
  })

  const events = useMemo(() => {
    if (!activityList) return []
    return activityList
      .filter((a) => !!a.dateStart && !Number.isNaN(new Date(a.dateStart).getTime()))
      .map((activity) => ({
        id: String(activity.idfActivity),
        title: activity.name,
        start: activity.dateStart,
        end: activity.dateStart,
        color: getActivityColor(activity.type),
        extendedProps: { activity },
      }))
  }, [activityList])

  // 외부 nav가 currentDate 변경 시 FC에도 이동 반영
  useEffect(() => {
    const api = calendarRef.current?.getApi()
    if (!api) return
    const key = format(currentDate, "yyyy-MM")
    const viewKey = format(api.getDate(), "yyyy-MM")
    if (key !== viewKey) api.gotoDate(currentDate)
  }, [currentDate])

  return (
    <div ref={containerRef} className="ActivityCalendarWidget">
      {isLoading && (
        <div className="ActivityCalendarWidget__loading">
          <CircularProgress size={24} />
        </div>
      )}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        initialDate={currentDate}
        events={events}
        headerToolbar={false}
        height="100%"
        fixedWeekCount={false}
        dayMaxEvents={true}
        displayEventTime={false}
        eventDisplay="list-item"
        eventClick={
          isPlayerAccount
            ? undefined
            : (info) => {
                info.jsEvent.preventDefault()
                openDetail(info.event.extendedProps.activity as Activity, info.el as HTMLElement)
              }
        }
      />
      {detailState.activity && (
        <ActivityInfoDialog
          open={detailState.open}
          anchorEl={detailState.anchorEl}
          activity={detailState.activity}
          onClose={closeDetail}
          onEdit={() => {}}
        />
      )}
    </div>
  )
}
